import {
  Button,
  Card,
  Dialog,
  DialogContent,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  makeStyles
} from '@material-ui/core'

import Alert from '@material-ui/lab/Alert'
import { FormattedDateTime } from '../formatted-date-time'
import React from 'react'
import { Spacer } from '@vestaboard/installables'
import { SubscriptionSimulateControls } from './SubscriptionSimulateControls'
import { TruncatedId } from '../truncated-id'
import { useEditSubscriptionMuted } from '../../api'
import { useSnackbar } from 'material-ui-snackbar-provider'

interface IChannelMutedToggleProps {
  id: string
  muted?: boolean | null
}

const ChannelEnabledToggle: React.FC<IChannelMutedToggleProps> = props => {
  const [enabled, setEnabled] = React.useState(!props.muted)
  const [editSubscriptionMuted] = useEditSubscriptionMuted()
  const snackbar = useSnackbar()

  const set = (v: boolean) => {
    setEnabled(v)
    editSubscriptionMuted({ variables: { subscriptionId: props.id, muted: !v } })
    snackbar.showMessage(`Channel ${props.id} was ${v ? 'enabled' : 'muted'}`)
  }

  return (
    <>
      <Switch checked={enabled} onChange={e => set(e.target.checked)} />
    </>
  )
}

interface ISubscription {
  id: string
  title?: string | null
  muted?: boolean | null
  created: string
  messageSet?: {
    id: string
  } | null
  configurationUrl?: string | null
  marketplaceListing?: {
    id: string
    title?: string | null
  } | null
  installation?: {
    id: string
    marketplaceListing?: {
      title?: string | null
    } | null
    installable?: {
      id: string
      title?: string | null
    } | null
  } | null
}

interface IChannelsProps {
  subscriptions?: ISubscription[] | null
  className?: string
  group?: boolean
}

const useStyles = makeStyles({
  devicesTitle: {
    paddingTop: 32,
    paddingBottom: 14
  }
})

export const Channels: React.FC<IChannelsProps> = ({ subscriptions, className, group }) => {
  const classes = useStyles()
  const [simulateSubscriptionConfig, setSimulateSubscriptionConfig] = React.useState('')

  const subscriptionRows = subscriptions
    ?.filter(s => !!s.installation)
    ?.map(subscription => (
      <TableRow key={subscription.id}>
        <TableCell>
          <TruncatedId value={subscription.id} path={`/subscription/${subscription.id}`} />
        </TableCell>
        <TableCell>
          <FormattedDateTime value={parseInt(subscription.created)} />
        </TableCell>
        <TableCell>
          {subscription.installation?.id ? (
            <TruncatedId
              path={`/installation/${subscription.installation?.id}`}
              value={subscription.installation?.id}
            />
          ) : (
            '(none)'
          )}
        </TableCell>
        {!group ? <TableCell>{subscription.title}</TableCell> : null}
        <TableCell>
          <TruncatedId
            value={subscription.installation?.installable?.title || ''}
            path={`/installables/${subscription.installation?.installable?.id}`}
          />
        </TableCell>
        {!group ? (
          <TableCell>
            {subscription.messageSet?.id ? (
              <TruncatedId value={subscription.messageSet?.id} path={`/message-sets/${subscription.messageSet?.id}`} />
            ) : null}
          </TableCell>
        ) : null}
        <TableCell>
          <TruncatedId
            value={subscription.marketplaceListing?.title || ''}
            path={`/marketplace-listings/${subscription.marketplaceListing?.id}`}
          />
        </TableCell>
        <TableCell>
          <SubscriptionSimulateControls
            subscriptionId={subscription.id}
            configurationUrl={subscription.configurationUrl}
            onSimulate={setSimulateSubscriptionConfig}
            noConfigFallback={<span />}
          />
        </TableCell>
        <TableCell>
          <ChannelEnabledToggle {...subscription} />
        </TableCell>
      </TableRow>
    ))

  return (
    <>
      <Typography variant='h5' className={className || classes.devicesTitle}>
        Channels (f.k.a. Subscriptions)
      </Typography>
      <Card>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Channel ID (f.k.a. Subscription ID)</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Installation ID</TableCell>
              {!group ? <TableCell>User-Defined Title</TableCell> : null}
              <TableCell>Installable Title</TableCell>
              {!group ? <TableCell>Message Set ID</TableCell> : null}
              <TableCell>Marketplace Listing</TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{subscriptionRows}</TableBody>
        </Table>
        {!subscriptions?.length && (
          <Alert severity='info'>There are no channels (f.k.a. subscriptions) associated with this board.</Alert>
        )}
      </Card>

      <Dialog fullWidth maxWidth='md' open={!!simulateSubscriptionConfig}>
        <DialogContent>
          <iframe
            style={{ width: '100%', height: 600, border: 'none' }}
            src={simulateSubscriptionConfig}
            title='Simulate Configuration'
          />
          <Spacer size='large' />
          <Button fullWidth onClick={() => setSimulateSubscriptionConfig('')}>
            Close
          </Button>
        </DialogContent>
      </Dialog>
    </>
  )
}
